import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button, Box } from '@chakra-ui/react'
import ModalWithSections from '~editor/ModalWithSections'
import {
  Title,
  Label,
  Button as ModalButton,
} from '~editor/ModalWithSections/components'
import {
  GlassPassLockIcon,
  KeyboardIcon,
  TapIcon,
} from '~editor/components/icons'
import { useSelector } from 'react-redux'
import { getSelectedNode } from '~editor/selectors/nodes'
import {
  DragDropContext,
  DropResult,
  ResponderProvided,
} from 'react-beautiful-dnd'
import ActionsList from '~editor/ActionEditor/ActionsList'
import useDispatch from '~editor/hooks/useDispatch'
import { AclEnum } from '~core/global.type'
import { EditorContext } from '~editor/contexts/editor-context'
import { useUser, useUserAbilities } from '~editor/hooks/useUser'
import { library } from '~core/componentLibrary'
import { addSpaceBeforeCapitalLetters } from '~utils/domHelpers'
import { getShard } from '~editor/selectors/shard'
import { RootState } from '~editor/store'
import {
  ActionGestureTypes,
  ForgeComponent,
  Actions,
  ActionCollections,
} from '~core/forge.type'
import { openGlassPassPage } from '~editor/utils/externalLinkHelpers'
import { useVersions } from '~editor/hooks/useVersions'
import { lt } from 'semver'

interface ActiveActionGestureProps {
  activeActionGesture: ActionGestureTypes
  setActiveActionGesture: React.Dispatch<
    React.SetStateAction<ActionGestureTypes>
  >
}

enum NavigationItems {
  Keybind = 'Keybind',
  MouseButton = 'MouseButton',
  MouseMove = 'MouseMove',
  Play = 'Sound Effect',
}

interface PanelProps extends ActiveActionGestureProps {}

const PANELS: Record<
  NavigationItems | 'Default',
  React.ComponentType<PanelProps>
> = {
  Default: ({ activeActionGesture, setActiveActionGesture }) => {
    const dispatch = useDispatch()
    const actions = useSelector(
      (state: RootState) => getSelectedNode(state).actions,
    )
    const dragging = useRef(false)

    const onDragStart = () => {
      dragging.current = true
    }

    const onDragEnd = (result: DropResult, provided: ResponderProvided) => {
      dragging.current = false

      if (!result.destination) return

      const newActions = Array.from(actions?.[activeActionGesture] || [])

      const [_removed] = newActions.splice(result.source.index, 1)
      const removed = { ..._removed }

      newActions.splice(result.destination.index, 0, removed)

      dispatch.nodes.updateActions({
        actionGestureType: activeActionGesture,
        actions: newActions,
      })
    }

    return (
      <Box height="100%" display="flex" flexDirection="column">
        <ActionsNavigation
          activeActionGesture={activeActionGesture}
          setActiveActionGesture={setActiveActionGesture}
        />
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <ActionsList
            activeActionGesture={activeActionGesture}
            actions={actions?.[activeActionGesture] || []}
          />
        </DragDropContext>
      </Box>
    )
  },
  [NavigationItems.Keybind]: () => <h1>Keybind</h1>,
  [NavigationItems.MouseButton]: () => <h1>MouseButton</h1>,
  [NavigationItems.MouseMove]: () => <h1>MouseMove</h1>,
  [NavigationItems.Play]: () => <h1>Play</h1>,
}

interface ActionsNavigationProps extends ActiveActionGestureProps {}

const getGestureLabel = (
  component: ForgeComponent,
  gestureType: ActionGestureTypes,
) => {
  return (
    (component?.forge?.actionsMetas as any)?.[gestureType]?.label ||
    addSpaceBeforeCapitalLetters(gestureType)
  )
}

const ActionsNavigation = ({
  activeActionGesture,
  setActiveActionGesture,
}: ActionsNavigationProps) => {
  const node = useSelector(getSelectedNode)
  const user = useUser()
  const component = useMemo(() => {
    return library.get(node.type)
  }, [node.type])

  const [isDown, setIsDown] = useState(false)
  const containerRef = useRef<HTMLElement>()
  const startX = useRef(0)
  const scrollLeft = useRef(0)

  return (
    <Box
      ref={containerRef as any}
      display="flex"
      position="relative"
      overflow="auto"
      cursor={isDown ? 'grabbing' : 'pointer'}
      onMouseDown={(e) => {
        setIsDown(() => true)
        startX.current = e.pageX - containerRef.current!.offsetLeft
        scrollLeft.current = containerRef.current!.scrollLeft
      }}
      onMouseLeave={() => {
        setIsDown(() => false)
      }}
      onMouseUp={() => {
        setIsDown(() => false)
      }}
      onMouseMove={(e) => {
        if (!isDown) return
        e.preventDefault()
        const x = e.pageX - containerRef.current!.offsetLeft
        const difference = x - startX.current
        containerRef.current!.scrollLeft = scrollLeft.current - difference
      }}
    >
      {/* noop button to get the height */}
      <Button variant="tab" opacity="0">
        {'NOOP'}
      </Button>

      <Box
        display="flex"
        flexWrap="nowrap"
        minWidth="100%"
        position="absolute"
        top="0"
        left="0"
      >
        {(
          Object.keys(component?.forge?.actions || {}) as ActionGestureTypes[]
        ).map((gestureType) => {
          const isActive = activeActionGesture === gestureType

          if (
            component?.forge?.actionsMetas &&
            gestureType in component?.forge?.actionsMetas
          ) {
            const meta = component?.forge?.actionsMetas[gestureType]

            if (
              // @ts-ignore
              'acl' in meta &&
              // @ts-ignore
              ((meta.acl.includes(AclEnum.STAFF) && !user?.is_staff) ||
                // @ts-ignore
                (meta.acl.includes(AclEnum.GLASSPASS) && !user?.glasspass))
            ) {
              return null
            }
          }

          return (
            <Button
              minWidth={'auto'}
              key={gestureType}
              variant="tab"
              isActive={isActive}
              cursor={isDown ? 'grabbing' : 'pointer'}
              onClick={() => {
                setActiveActionGesture(
                  ActionGestureTypes[gestureType] || gestureType,
                )
              }}
            >
              {getGestureLabel(component, gestureType)}
            </Button>
          )
        })}
      </Box>
    </Box>
  )
}

const UpgradeModalButton = (
  props: PropsWithChildren<React.ComponentProps<typeof ModalButton>>,
) => {
  const [hover, setHover] = useState(false)
  return (
    <ModalButton
      {...props}
      disabled={true}
      hasAddLabel={false}
      onClick={() => openGlassPassPage()}
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      rightIcon={<GlassPassLockIcon />}
      _hover={{
        fontWeight: 700,
        color: '#FFF',
        background:
          'linear-gradient(270deg, rgba(255, 179, 71, 0.2) 0%, rgba(255, 89, 71, 0.2) 100%)',
        border: '1px solid #FF8B47',
        boxShadow: '0px 4px 15px rgba(255, 139, 71, 0.25)',
      }}
    >
      {hover ? 'Upgrade to Unlock' : props.children}
    </ModalButton>
  )
}

const ActionEditor = () => {
  const dispatch = useDispatch()
  const { shard } = useSelector(getShard)
  const {
    ActionEditor: { isOpen, onClose },
  } = useContext(EditorContext)
  const selectedNode = useSelector(getSelectedNode)
  const component = useMemo(() => {
    return library.get(selectedNode.type)
  }, [selectedNode.type])
  const [navigationItems, setNavigationItems] = useState<NavigationItems[]>([])
  const [activeActionGesture, setActiveActionGesture] = useState(
    (Object.keys(component?.forge?.actions || {}) as ActionGestureTypes[])?.[0],
  )

  useEffect(() => {
    setActiveActionGesture(
      (
        Object.keys(component?.forge?.actions || {}) as ActionGestureTypes[]
      )?.[0],
    )
  }, [selectedNode.id])

  const user = useUser()
  const abilities = useUserAbilities()
  const versions = useVersions()

  const createNewAction = useCallback(
    (Action: typeof Actions[keyof typeof Actions]) => {
      const action = new Action()

      dispatch.nodes.addAction({
        actionGestureType: activeActionGesture,
        action,
      })
    },
    [activeActionGesture],
  )

  const headerTitle = useMemo(() => {
    return (
      <Title leftIcon={<TapIcon color="gameglass.50" />}>{'Actions'}</Title>
    )
  }, [])

  const headerBody = useMemo(() => {
    const Separator = () => <Box margin={'0 5px'}>{'>'}</Box>
    const newNavigation: any[] = []
    const keySuffix = +new Date()

    ;[
      <Title
        key={`${selectedNode.type}${keySuffix}`}
        width={'auto'}
        leftIcon={<KeyboardIcon />}
      >
        {selectedNode.type}
      </Title>,
      <ModalButton
        key={`ActionEditor${keySuffix}`}
        width={'auto'}
        color={navigationItems.length ? 'ui.text.grey' : undefined}
        onClick={() => setNavigationItems([])}
      >
        {'Action Editor'}
      </ModalButton>,
      ...navigationItems,
    ].forEach((item, i, items) => {
      newNavigation.push(
        typeof item === 'string' ? (
          <ModalButton key={'ActionEditor'} width={'auto'}>
            {item}
          </ModalButton>
        ) : (
          item
        ),
      )

      if (i !== items.length - 1) {
        newNavigation.push(<Separator key={`Separator${i}`} />)
      }
    })

    return (
      <Box display="flex" alignItems="center">
        {newNavigation}
      </Box>
    )
  }, [selectedNode, navigationItems])

  const sideBody = useMemo(() => {
    const canAddActions = abilities?.can('add', 'NodeActions') || false

    return Object.entries(
      Object.entries(Actions).reduce((acc, [name, value]) => {
        const collection = value.getCollection()
        const meta = value.getMeta()

        if (meta.acl?.includes(AclEnum.STAFF) && !user?.is_staff) {
          return acc
        }

        if (
          !user?.is_staff &&
          meta.minVersion &&
          (!versions?.hub || lt(versions.hub, meta.minVersion))
        ) {
          return acc
        }

        const showUpgrade =
          meta.acl?.includes(AclEnum.GLASSPASS) && !user?.glasspass

        acc[collection] = acc[collection] || []
        acc[collection].push(
          showUpgrade ? (
            <UpgradeModalButton
              key={name}
              className={meta.label === 'Keybind' ? 'keybindButton' : ''}
              leftIcon={<meta.icon />}
            >
              {meta.label}
            </UpgradeModalButton>
          ) : (
            <ModalButton
              key={name}
              disabled={!canAddActions}
              hasAddLabel={canAddActions}
              className={meta.label === 'Keybind' ? 'keybindButton' : ''}
              onClick={() => {
                createNewAction(value)
              }}
              leftIcon={<meta.icon />}
            >
              {meta.label}
            </ModalButton>
          ),
        )

        return acc
      }, {} as Record<ActionCollections, JSX.Element[]>),
    ).map(([collection, elements]) => {
      if (elements?.length <= 0) {
        return null
      }

      return (
        <React.Fragment key={collection}>
          <Label>{collection}</Label>
          {elements}
        </React.Fragment>
      )
    })
  }, [createNewAction, abilities, shard, versions])

  const mainBody = useMemo(() => {
    const Panel = PANELS[navigationItems[0]] || PANELS.Default

    return (
      <Panel
        activeActionGesture={activeActionGesture}
        setActiveActionGesture={setActiveActionGesture}
      />
    )
  }, [navigationItems, activeActionGesture, setActiveActionGesture])

  const footer = useMemo(() => {
    return (
      <Box
        display="flex"
        flexDirection="row-reverse"
        flex="1"
        justifyContent="space-between"
        alignItems="center"
      >
        <Button variant="primary" className="doneButton" onClick={onClose}>
          {'Done'}
        </Button>

        {!!selectedNode?.actions?.[activeActionGesture]?.length &&
          abilities?.can('manage', 'NodeActions') && (
            <Button
              variant="warning"
              onClick={() => {
                dispatch.nodes.updateActions({
                  actionGestureType: activeActionGesture,
                  actions: [],
                })
              }}
            >
              {`Clear ${getGestureLabel(
                component,
                activeActionGesture,
              )} Actions`}
            </Button>
          )}
      </Box>
    )
  }, [selectedNode?.actions?.[activeActionGesture], abilities])

  return (
    (isOpen || null) && (
      <ModalWithSections
        isOpen={isOpen}
        onClose={onClose}
        headerTitle={headerTitle}
        headerBody={headerBody}
        sideBody={sideBody}
        mainBody={mainBody}
        footer={footer}
      />
    )
  )
}

export default ActionEditor
